import type {
 Messages } from '../catalogue.en'

/**
 * The Control Center core and the account-profile workspaces, their
 * registries and personal sections. Access administration lives in
 * `controlCenterAccess`, external services in `controlCenterIntegrations`.
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing
 * imports this module directly: consumers ask the catalogue, so how the
 * messages are stored stays this layer's business.
 */
export const controlCenter: Partial<Messages> = {
  "controlCenter.administration.scopeSharedProfiles": "פרופילי מילונים משותפים",
  "controlCenter.administration.platformAI": "שירותי וכלי בינה מלאכותית",
  "controlCenter.administration.platformPrintForms": "כלים לטופסי הדפסה",
  "controlCenter.administration.platformTerminology": "הגדרת מילונים",
  "controlCenter.administration.platformQuotas": "מגבלות המרפאה",
  "controlCenter.administration.platform": "ניהול הפלטפורמה",
  "controlCenter.administration.platformDescription": "משאבים משותפים וכלים טכניים",
  "controlCenter.administration.organizationGroup": "ארגון",
  "controlCenter.administration.teamGroup": "עובדים והרשאות",
  "controlCenter.administration.formsGroup": "טפסים ומילונים",
  "controlCenter.administration.connectionsGroup": "חיבורים ובינה מלאכותית",
  "controlCenter.administration.registration": "רישום מטופלים",
  "controlCenter.administration.registrationDescription": "שאלון, רופא בודק וכניסת המטופל",
  "controlCenter.administration.scopePlatform": "חל על כל הפלטפורמה",
  "controlCenter.administration.scopeClinic": "מרפאה",
  "controlCenter.administration.scopePersonalClinic": "ההגדרות שלך במרפאה",
  "controlCenter.administration.scopeAccount": "החשבון שלך",
  "controlCenter.administration.breadcrumb": "המיקום שלך",
  "controlCenter.administration.sharedTerminology": "פרופילים אלה עשויים לשמש כמה מרפאות. שינוי פרופיל משותף משנה את המילונים בכל המרפאות המשתמשות בו.",
  "controlCenter.administration.aiTools": "כלי בינה מלאכותית",
  "controlCenter.administration.aiToolsDescription": "בדיקת מסמכים ואבחון טכני",
  "controlCenter.administration.security": "כניסה ואבטחה",
  "controlCenter.administration.securityDescription": "שם משתמש, סיסמה ודוא״ל מאומת של החשבון",
  "controlCenter.administration.communications": "התראות ותקשורת",
  "controlCenter.administration.communicationsDescription": "יישומי מסרים, שעות התראות וחתימה להודעות",

  'onboardingSettings.title': 'קליטת מטופלים',
  'onboardingSettings.loading': 'הגדרות הקליטה בטעינה',
  'onboardingSettings.loadFailed': 'לא ניתן לטעון את הגדרות הקליטה',
  'onboardingSettings.saveFailed': 'לא ניתן לשמור את הגדרות הקליטה.',
  'onboardingSettings.invalid': 'השאלון או הרופא הבודק אינם זמינים עוד בארגון זה.',
  'onboardingSettings.unsavedIntent': 'הגדרות קליטת מטופלים',
  'onboardingSettings.protocol': 'שאלון',
  'onboardingSettings.reviewer': 'רופא בודק',
  'onboardingSettings.unassigned': 'ללא שיוך; הודעה לבעלים',
  'onboardingSettings.chooseProtocol': 'בחירת שאלון',
  'onboardingSettings.unavailable': 'לא זמין',
  "controlCenter.history.title": "מה חדש",
  'controlCenter.history.description': "משימות שנכללו בגרסאות שפורסמו. שינויים ללא מספר משימה אינם מופיעים.",
  'controlCenter.history.loading': "היסטוריית העדכונים נטענת…",
  'controlCenter.history.empty': "עדיין אין עדכונים שפורסמו.",
  'controlCenter.history.noTasks': "אין משימות מתועדות בגרסה זו.",
  'controlCenter.history.more': "עדכונים קודמים",
  'controlCenter.history.unavailable': "היסטוריית העדכונים אינה זמינה.",
  "controlCenter.title": "ניהול המרפאה",
  "controlCenter.subtitle": "הגדרות המרפאה שנבחרה.",
  "controlCenter.navLabel": "תחומי ניהול המרפאה",
  "controlCenter.nav.organization": "פרטי המרפאה",
  "controlCenter.nav.organizationDescription": "הישות המשפטית המפעילה ופרטי המרפאה",
  "controlCenter.nav.uploads": "תור ייבוא בדיקות דימות",
  'controlCenter.nav.uploadsDescription': 'העברות וייבואי דימות שלא הושלמו',
  "controlCenter.nav.users": "עובדים",
  'controlCenter.nav.usersDescription': 'חשבונות, תפקידים וגישה',
  "controlCenter.nav.workplaces": "עבודה בסניפים",
  'controlCenter.nav.workplacesDescription': 'סניפים, מקצועות והתמחויות של קלינאים',
  "controlCenter.nav.roles": "תפקידים והרשאות",
  'controlCenter.nav.rolesDescription': 'חבילות תפקידים וההרשאות שהן מעניקות',
  "controlCenter.nav.settings": "זמן, לוח שנה ותבניות תצוגה",
  "controlCenter.nav.settingsDescription": "אזור הזמן, לוח העבודה ותבניות התצוגה של המרפאה",
  "controlCenter.nav.terminology": "מילונים וסיווגים",
  'controlCenter.nav.terminologyDescription': 'מילונים וגרסאות לכל חלק',
  "controlCenter.nav.modules": "מודולי המרפאה",
  'controlCenter.nav.modulesDescription': 'מודולים משובצים: פעיל, כבוי או מוסתר',
  "controlCenter.nav.connectors": "ערוצי מסירת הודעות",
  'controlCenter.nav.connectorsDescription': 'מחברי דוא"ל, Telegram ו-WhatsApp ופרטי הגישה שלהם',
  'controlCenter.nav.printForms': 'טפסים להדפסה',
  'controlCenter.nav.printFormsDescription': 'טפסים רשומים ועורך',
  "controlCenter.nav.protocols": "טפסי רשומות ושאלונים",
  "controlCenter.nav.protocolsDescription": "הגדרות, גרסאות וכללי בחירה",
  "controlCenter.nav.ai": "פונקציות בינה מלאכותית",
  'controlCenter.nav.aiDescription': 'מנועים, מרשם פרומפטים וקובצי תמיכה',
  'controlCenter.discard': 'ביטול שינויים',

  "controlCenter.uploads.title": "תור ייבוא בדיקות דימות",
  'controlCenter.uploads.subtitle': 'כל ייבואי הדימות שלא הושלמו במרפאה, כולל העברות מושהות ומחקרים הממתינים לאישור.',
  'controlCenter.uploads.loading': 'טוען העלאות',
  'controlCenter.uploads.failed': 'לא ניתן לטעון את ההעלאות. אף שורה לא הוסתרה במכוון.',
  'controlCenter.uploads.forbidden': 'אין לך הרשאה לקרוא את העלאות הדימות של המרפאה',
  'controlCenter.uploads.tableLabel': 'העלאות דימות שלא הושלמו',
  'controlCenter.uploads.empty': 'אין העלאות שלא הושלמו',
  'controlCenter.uploads.emptyDetail': 'ייבואי דימות חדשים יופיעו כאן מיד לאחר שהשרת יקבל אותם.',
  'controlCenter.uploads.noMatches': 'אין העלאות התואמות למסננים',
  'controlCenter.uploads.patient': 'מטופל',
  'controlCenter.uploads.source': 'מקור',
  'controlCenter.uploads.uploader': 'נוסף על ידי',
  'controlCenter.uploads.updated': 'עודכן',
  'controlCenter.uploads.openPatientFiles': 'פתיחת קובצי המטופל',
  'controlCenter.uploads.cancel': 'ביטול ומחיקה',

  "organization.title": "פרטי המרפאה",
  'branches.title': 'סניפים',
  'branches.name': 'שם',
  'branches.address': 'כתובת',
  'branches.default': 'סניף ברירת מחדל',
  'branches.active': 'פעיל',
  'branches.archived': 'בארכיון',
  'branches.create': 'סניף חדש',
  'branches.save': 'שמירת סניף',
  'branches.archive': 'העברה לארכיון',
  'branches.archiveConfirm': 'להעביר את הסניף לארכיון? היסטוריית הביקורים תישמר.',
  'branches.empty': 'אין סניפים',
  'branches.loadFailed': 'לא ניתן לטעון את הסניפים.',
  'branches.failed': 'לא ניתן לשמור את הסניף. הנתונים שהוזנו נשמרו בטופס.',
  'branches.forbidden': 'אין לך הרשאה לשנות סניפים.',
  'branches.invalid': 'יש לבדוק את שם הסניף ואת כתובתו.',
  'branches.changed': 'פרטי הסניף השתנו בזמן שהטופס היה פתוח. יש לטעון את הגרסה העדכנית.',
  'branches.defaultRequired': 'יש לבחור תחילה סניף פעיל אחר כברירת מחדל.',
  'branches.inUse': 'בסניף יש תורים עתידיים, משאבים פעילים או ביקורים שלא הסתיימו.',
  'branches.reload': 'טעינה מחדש של הסניף',
  'branches.reloaded': 'פרטי הסניף העדכניים נטענו. יש לבדוק אותם לפני השמירה.',
  'branches.unsaved': 'פרטי סניף שלא נשמרו',
  'branches.subtitle': 'המקומות שבהם המרפאה מקבלת מטופלים.',
  'organization.subtitle': 'הזהות המשפטית המפעילה את המרפאה והגרסאות השמורות שלה.',
  'organization.loading': 'טוען את הארגון',
  'organization.loadFailed': 'לא ניתן לטעון את הארגון',
  'organization.forbidden': 'אין לך הרשאה לקרוא את פרטי הארגון של המרפאה',
  'organization.name': 'שם הארגון',
  'organization.legalName': 'שם משפטי',
  'organization.country': 'מדינה',
  'organization.addressLine1': 'כתובת',
  'organization.addressLine2': 'פרטי כתובת',
  'organization.city': 'עיר',
  'organization.postalCode': 'מיקוד',
  'organization.phone': 'טלפון',
  'organization.email': 'אימייל',
  'organization.website': 'אתר',
  'organization.taxType': 'סוג מזהה מס',
  'organization.taxNone': 'לא הוגדר',
  'organization.taxIlCompany': 'מספר חברה ישראלי',
  'organization.taxIlVat': 'מספר עוסק ישראלי',
  'organization.taxIlIdentity': 'מספר זהות ישראלי',
  'organization.taxOther': 'מזהה אחר',
  'organization.taxValue': 'מזהה מס',
  'organization.identityLeaf': 'פרטי זיהוי',
  'organization.addressLeaf': 'כתובת',
  'organization.contactsLeaf': 'פרטי קשר',
  'organization.validityLeaf': 'תוקף',
  'organization.save': 'שמירת ארגון',
  'organization.saving': 'שומר',
  'organization.saved': 'הארגון נשמר',
  'organization.unsavedIntent': 'ארגון',
  'organization.conflict': 'מישהו אחר שינה את הארגון בזמן שהטופס היה פתוח.',
  'organization.saveFailed': 'לא ניתן לשמור את הארגון.',

  "profile.workspaceTitle": "הפרופיל שלי",
  'profile.workspaceSubtitle': 'מידע אישי והגדרות החשבון שלך.',
  'profile.navLabel': 'חלקי הפרופיל',
  "profile.nav.personal": "פרטים אישיים",
  "profile.nav.personalDescription": "שמות, פרטי קשר ועבודה במרפאה",
  "profile.nav.tasks": "אזכורים שלי",
  'profile.nav.tasksDescription': 'משימות שמזכירות אותך',
  "profile.nav.settings": "גישה אישית לבינה מלאכותית",
  'profile.nav.settingsDescription': 'גישת ה-AI האישית שלך',
  "profile.group.account": "חשבון",
  "profile.group.connections": "חיבורים ובינה מלאכותית",
  "profile.group.development": "פיתוח",
  "profile.nav.designDrafts": "טיוטות עיצוב",
  "profile.nav.designDraftsDescription": "הצעות ממשק שעוזר שמר",
  'profile.tasksEmpty': 'אין משימות שמזכירות אותך',
  'profile.tasksEmptyDetail': 'משימות שבהן אתה נמען של ההודעה יופיעו כאן.',
  'profile.personalTitle': 'מידע אישי',
  "profile.personalSubtitle": "השמות, פרטי הקשר והעבודה שלך במרפאה שנבחרה.",
  'profile.professionalTitle': 'סיווג קליני',
  'profile.loading': 'טוען את הפרופיל שלך',
  'profile.loadFailed': 'לא ניתן לטעון את הפרופיל שלך',
  'profile.unsavedIntent': 'פרופיל',
  'profile.messageSignature': 'חתימה להודעות',
  'profile.messageSignatureHint': 'מוכנה מראש ומצורפת לכל הודעה שאתם שולחים למטופל.',
  'profile.passwordTitle': 'סיסמה',
  'profile.passwordEnrollTitle': 'הגדרת סיסמה',
  'profile.passwordEnrollmentUnsaved': 'סיסמה ראשונה',
  'profile.passwordOpenTelegram': 'אימות בטלגרם',
  'profile.passwordTelegramFresh': 'תוקף האימות בטלגרם פג או שהוא שייך לחשבון אחר. יש לפתוח מחדש את יישומון הצוות.',
  'profile.passwordTelegramUnavailable': 'האימות בטלגרם אינו זמין.',
  'profile.passwordAlreadySet': 'לחשבון הזה כבר הוגדרה סיסמה.',
  'profile.passwordEnrollmentThrottled': 'יותר מדי ניסיונות. אפשר לנסות שוב בעוד 15 דקות.',
  'profile.passwordEnrollmentRetry': 'ניסיון חוזר',
  'profile.passwordRequired': 'נדרש שינוי סיסמה לפני המשך עבודה.',
  'profile.changePassword': 'שינוי סיסמה',
  'profile.currentPassword': 'סיסמה נוכחית',
  'profile.newPassword': 'סיסמה חדשה',
  'profile.passwordSave': 'שמירת סיסמה',
  'profile.passwordSaving': 'שומר',
  'profile.passwordSaved': 'הסיסמה נשמרה',
  'profile.passwordWrong': 'הסיסמה הנוכחית אינה נכונה.',
  'profile.passwordFailed': 'לא ניתן לשמור את הסיסמה.',
  'profile.passwordUnsavedIntent': 'החלפת סיסמה \u2014 טרם הוחלה',

  "profileSettings.title": "גישה אישית לבינה מלאכותית",
  "profileSettings.subtitle": "מפתח OpenAI שלך למרפאה שנבחרה. הוא אינו משותף לחברויות שלך במרפאות אחרות.",
  'profileSettings.openAISecretTitle': 'מפתח OpenAI',
  'profileSettings.secretUnsavedIntent': 'מפתח OpenAI',
  'profileSettings.secretLoading': 'טוען מצב מפתח OpenAI',
  'profileSettings.secretUnavailable': 'אחסון הסודות אינו מוגדר.',
  'profileSettings.secretLoadFailed': 'לא ניתן לטעון את מצב מפתח OpenAI.',
  'profileSettings.key': 'מפתח',
  'profileSettings.secretSet': 'הגדרת מפתח',
  'profileSettings.secretChange': 'החלפת מפתח',
  'profileSettings.openAISecret': 'מפתח API של OpenAI',
  'profileSettings.secretSave': 'שמירת מפתח',
  'profileSettings.secretSaving': 'שומר',
  'profileSettings.secretClear': 'מחיקת מפתח',
  'profileSettings.secretClearing': 'מוחק',
  'profileSettings.secretSaved': 'מפתח OpenAI נשמר',
  'profileSettings.secretCleared': 'מפתח OpenAI נמחק',
  'profileSettings.secretSaveFailed': 'לא ניתן לשמור את מפתח OpenAI.',
  'profileSettings.secretClearFailed': 'לא ניתן למחוק את מפתח OpenAI.',

  "clinicSettings.title": "זמן, לוח שנה ותבניות תצוגה",
  'clinicSettings.subtitle': 'זמן אזרחי ותצוגת תאריך ושעה לכל המרפאה.',
  'clinicSettings.loading': 'טוען את הגדרות המרפאה',
  'clinicSettings.loadFailed': 'לא ניתן לטעון את הגדרות המרפאה',
  'clinicSettings.forbidden': 'אין לך הרשאה לקרוא את הגדרות המרפאה',
  'clinicSettings.unsavedIntent': 'הגדרות המרפאה',
  'clinicSettings.timeZone': 'אזור הזמן של המרפאה',
  'clinicSettings.workingCalendar': 'לוח ימי העבודה של המרפאה',
  'clinicSettings.dateFormat': 'תבנית תאריך',
  'clinicSettings.dateFormat.dayMonthYear': '23/08/2026',
  'clinicSettings.dateFormat.monthDayYear': '08/23/2026',
  'clinicSettings.dateFormat.yearMonthDay': '2026-08-23',
  'clinicSettings.timeFormat': 'תבנית שעה',
  'clinicSettings.timeFormat.24Hour': '24 שעות (14:30)',
  'clinicSettings.timeFormat.12Hour': '12 שעות (2:30 PM)',
  'clinicSettings.numberFormat': 'תבנית מספר',
  'clinicSettings.numberFormat.decimalDotGroupComma': '1,234.56',
  'clinicSettings.numberFormat.decimalCommaGroupSpace': '1 234,56',
  'clinicSettings.numberFormat.decimalCommaGroupDot': '1.234,56',
  'clinicSettings.saveFailed': 'לא ניתן לשמור את הגדרות המרפאה.',

  'clinicSettings.civilTime': 'זמן אזרחי ולוח ימי עבודה',
  'clinicSettings.displayFormats': 'תבניות תצוגה',

  'clinicSettings.services': 'שירותים מחוברים',
  'clinicSettings.servicesSubtitle':
    'שירותים חיצוניים שהמרפאה מחזיקה עבורם סוד. סוד שנשמר אינו מוצג שוב — רק תחילתו וסופו, כדי שניתן יהיה לזהות את הסוד השמור.',

  'terminology.profile': "פרופיל",
  'terminology.profileSetupRequired': 'פרופיל המינוח לא הוגדר. נדרשת הגדרה על ידי מפתח.',
  'terminology.profileSelected': "הפרופיל הנבחר",
  'terminology.profileSelect': "בחירה למרפאה",
  'terminology.profileEdit': "עריכת פרופיל",
  'terminology.profileCopy': "יצירת עותק",
  'terminology.profileDelete': "מחיקת פרופיל שאינו בשימוש",
  'terminology.profileName': "שם",
  'terminology.profileCountry': "מדינה (קוד ISO)",
  'terminology.profileShared': "השינויים חלים על כל המרפאות המשתמשות בפרופיל.",
  'terminology.profileUnsaved': "פרופיל מינוח שלא נשמר",
  'terminology.profileConflict': "הפרופיל או הבחירה במרפאה השתנו. יש לטעון את הגרסה השמורה לפני השמירה.",
  'terminology.profileReload': "טעינה מחדש וביטול שינויי הפרופיל",
  'terminology.profileReloadFailed': "הטעינה מחדש נכשלה. הטיוטה נשמרה.",
  'terminology.profileReloaded': "הפרופיל השמור והבחירה במרפאה נטענו. יש לבדוק אותם לפני השמירה.",
  'terminology.profileFailed': "השמירה נכשלה. יש לבדוק את המדינה, המרפאות המקושרות והגרסה העדכנית; הטיוטה נשמרת.",
  'terminology.profileCreate': "יצירת פרופיל",
  'terminology.profileUnbind': "הסרת שיוך",
  "terminology.title": "מילונים וסיווגים",
  'terminology.subtitle': 'איזה מילון כל חלק במוצר קורא, ובאיזו גרסה.',
  'terminology.loading': 'טוען את שיוכי הטרמינולוגיה',
  'terminology.loadFailed': 'לא ניתן לטעון את שיוכי הטרמינולוגיה',
  'terminology.forbidden': 'אין לך הרשאה לקרוא את שיוכי הטרמינולוגיה של מרפאה זו',
  'terminology.serviceUnavailable':
    'שירות הטרמינולוגיה אינו מגיב. השיוכים שלהלן הם מה שהמרפאה שמרה; לא ניתן לבחור מילון או גרסה עד שהשירות יחזור.',

  'terminology.openApiReference': 'פתיחת תיעוד ה-API של הטרמינולוגיה',
  'terminology.openApiReferenceNote': 'התיעוד נפתח בלשונית חדשה. הוא מתאר את שירות הטרמינולוגיה עצמו, לא את הרשם הזה.',

  'terminology.section': 'חלק',
  'terminology.sectionDiagnoses': 'אבחנות',
  'terminology.sectionOncologyTopography': 'טופוגרפיה אונקולוגית',
  'terminology.sectionOncologyTNM': 'אונקולוגיה: TNM',
  'terminology.sectionOncologyMorphology': 'מורפולוגיה אונקולוגית',
  'terminology.sectionLaboratory': 'מעבדה',
  'terminology.sectionServices': 'שירותים רפואיים',
  'terminology.sectionPractitionerProfessions': 'מקצועות מטפלים',
  'terminology.sectionOrganizationStructure': 'מבנה ארגוני',
  'terminology.sectionAllergyDrugSubstances': 'אנמנזה: אלרגנים תרופתיים',
  'terminology.sectionAllergyOtherSubstances': 'אנמנזה: אלרגנים אחרים',
  'terminology.sectionAllergyManifestations': 'אנמנזה: תגובות אלרגיות',
  'terminology.sectionHabits': 'אנמנזה: הרגלים',

  'terminology.dictionary': 'מילון',
  'terminology.dictionaryFor': 'מילון עבור {section}',
  'terminology.version': 'גרסה',
  'terminology.versionFor': 'גרסה עבור {section}',
  'terminology.versionFollows': 'לפי הגרסה הנוכחית',
  'terminology.versionUnpublished': '{version} (אינה מפורסמת עוד)',
  'terminology.versionsLoading': 'טוען גרסאות',
  'terminology.versionsFailed': 'לא ניתן לטעון את גרסאות המילון הזה',

  'terminology.resolvedVersion': 'בתוקף',

  'terminology.status': 'מצב',
  'terminology.statusActive': 'בשימוש',
  'terminology.statusRecorded': 'רשום בלבד',

  'terminology.updated': 'שונה',

  'terminology.actions': 'פעולות',
  'terminology.editNamed': 'עריכת {name}',
  'terminology.save': 'שמירה',
  'terminology.saving': 'שומר',
  'terminology.cancel': 'ביטול',
  'terminology.reload': 'קריאת הרשם מחדש',
  'terminology.unavailableEdit': 'שירות הטרמינולוגיה אינו מגיב',

  'terminology.saveFailed': 'לא ניתן לשמור את השיוך',
  'terminology.saveForbidden': 'אין לך הרשאה לשנות את שיוכי הטרמינולוגיה של מרפאה זו',
  'terminology.saveConflict': 'מישהו אחר שינה את השיוך הזה. קרא את הרשם מחדש והחלט מול מה שמאוחסן עכשיו.',
  'terminology.saveRejected': 'לא ניתן לשייך את המילון והגרסה האלה לחלק הזה',

  'terminology.tableLabel': 'שיוכי טרמינולוגיה',
  'terminology.empty': 'אף חלק במוצר אינו צורך טרמינולוגיה',
  'terminology.emptyDetail': 'רשימת החלקים מגיעה מהמוצר, לא מהמרפאה הזו.',
  'terminology.noMatches': 'אף חלק אינו תואם את המסננים',
  'terminology.noMatchesDetail': 'נקה את המסננים כדי לראות את כל החלקים.',

  "protocols.title": "טפסי רשומות ושאלונים",
  'protocols.subtitle': 'הגדרות שפורסמו, הפעלה ושיוך דטרמיניסטי למרפאה.',
  'protocols.loading': 'טוען פרוטוקולים וסוגי מפגש',
  'protocols.loadFailed': 'לא ניתן לטעון את רשם הפרוטוקולים',
  'protocols.forbidden': 'אין לך הרשאה לקרוא פרוטוקולים במרפאה הזו',
  'protocols.openEditor': 'פתיחת עורך הפרוטוקולים',
  'protocols.openEditorHint': 'העורך ייפתח בכרטיסייה חדשה עם החשבון הזה.',
  'protocols.releasesTitle': 'גרסאות רשומות',
  'protocols.releasesEmpty': 'אין גרסת MPE רשומה למרפאה הזו.',
  'protocols.protocol': 'פרוטוקול',
  'protocols.release': 'גרסה',
  'protocols.registered': 'נרשם',
  'protocols.state': 'מצב',
  'protocols.actions': 'פעולות',
  'protocols.active': 'פעיל',
  'protocols.inactive': 'לא פעיל',
  'protocols.activate': 'הפעלת גרסה',
  'protocols.deactivate': 'הסרה מהבחירה',
  'protocols.policyTitle': 'מדיניות בחירה',
  'protocols.policyHint':
    'כלל מדויק של מומחיות במקום העבודה וסוג מפגש יציב קודם. ברירת המחדל משמשת רק כשאין התאמה מדויקת.',
  'protocols.defaultLabel': 'ברירת מחדל למרפאה',
  'protocols.chooseProtocol': 'בחירת פרוטוקול פעיל',
  'protocols.saveDefault': 'שמירת ברירת מחדל',
  'protocols.expertise': 'מומחיות',
  'protocols.visitType': 'סוג מפגש',
  'protocols.chooseVisitType': 'בחירת סוג מפגש',
  'protocols.addRule': 'הוספת כלל מדויק',
  'protocols.rulesEmpty': 'לא הוגדרו כללי שיוך מדויקים.',
  'protocols.deleteRuleNamed': 'מחיקת כלל השיוך עבור {name}',
  'protocols.deleteSelected': 'מחיקת {count} שנבחרו',
  'protocols.deleteSelectedTitle': 'למחוק את כללי השיוך שנבחרו?',
  'protocols.deleteSelectedDescription':
    '{count} כללים יפסיקו לשייך פרוטוקול. במפגשים שכבר נפתחו יישמר הפרוטוקול שהוקצה להם.',
  'protocols.deleteSelectedConfirm': 'מחיקת {count} כללים',
  'protocols.deleteSelectedCancel': 'להשאיר את הבחירה',
  'protocols.rulesDeletedCount': '{count} כללי שיוך נמחקו.',
  'protocols.rulesDeletedBefore': '{count} כללים נמחקו.',
  'protocols.actionFailed': 'לא ניתן להשלים את הפעולה.',
  'protocols.activateFailed': 'לא ניתן להפעיל את הגרסה.',
  'protocols.defaultFailed': 'לא ניתן לשנות את ברירת המחדל למרפאה.',
  'protocols.createRuleFailed': 'לא ניתן להוסיף את הכלל.',
  'protocols.deleteRuleFailed': 'לא ניתן למחוק את הכלל.',
  'protocols.activateRequiresPrimary':
    'פרוטוקול שמדיניות הבחירה משתמשת בו חייב להישאר מסמך מפגש ראשי.',
  'protocols.defaultRequiresPrimary':
    'ברירת המחדל למרפאה חייבת להיות מסמך מפגש ראשי פעיל.',
  'protocols.releaseGone': 'הגרסה הזו אינה רשומה עוד. יש לרענן את המרשם.',
  'protocols.deactivateFailed': 'לא ניתן להסיר את הפרוטוקול מהבחירה.',
  'protocols.deactivateUsedByRule':
    'כלל שיוך עדיין מפנה לפרוטוקול הזה. יש למחוק את הכלל קודם.',
  'protocols.deactivateIsDefault':
    'הפרוטוקול הזה הוא ברירת המחדל של המרפאה. יש לבחור ברירת מחדל אחרת קודם.',
  'protocols.activationGone': 'הפרוטוקול הזה כבר אינו פעיל. יש לרענן את המרשם.',
  'protocols.ruleGone': 'הכלל הזה כבר נמחק. יש לרענן את המרשם.',
  'protocols.ruleExists': 'כבר קיים כלל למומחיות ולסוג המפגש האלה.',
  'protocols.ruleRejected':
    'הכלל נדחה. יש לבדוק את המומחיות, סוג המפגש והפרוטוקול.',
  'protocols.registryUnavailable': 'מרשם הפרוטוקולים אינו זמין. יש לנסות שוב בהמשך.',
  'protocols.suggestionStale': 'השם השמור השתנה. יש לפתוח את הסוג מחדש ולבקש הצעות חדשות.',
  'protocols.unsavedIntent': 'טופס תזמון \u2014 נתונים שלא נשמרו',
  'protocols.visitTypesTitle': 'סוגי מפגש',
  'protocols.visitTypesHint': 'מזהה הסוג היציב משמש בכללי פרוטוקול. גרסאות שפורסמו שומרות את היסטוריית השם והמשך.',
  'protocols.visitTypesForbidden': 'אין לך הרשאה לקרוא סוגי מפגש במרפאה הזו',
  'protocols.visitTypesEmpty': 'לא רשומים סוגי מפגש.',
  'protocols.visitTypeCode': 'קוד יציב',
  'protocols.visitTypeName': 'שם תצוגה',
  'protocols.duration': 'משך בדקות',
  'protocols.requirementCode': 'קוד דרישה',
  'protocols.capabilityCode': 'קוד יכולת',
  'protocols.requirementQuantity': 'כמות',
  'protocols.addRequirement': 'הוספת דרישת משאב',
  'protocols.removeRequirement': 'הסרה',
  'protocols.createVisitType': 'יצירת סוג מפגש',
  'protocols.versions': 'גרסאות שפורסמו',
  'protocols.versionSummary': 'גרסה {version} · {minutes} דקות',
  'protocols.publishVersion': 'פרסום גרסה',
  'protocols.rulesTableLabel': 'כללי שיוך מדויקים',
  'protocols.newRuleTitle': 'כלל שיוך חדש',
  'protocols.newVisitTypeTitle': 'סוג מפגש חדש',
  'protocols.publishVersionTitle': 'פרסום גרסה',

  'printForm.title': 'טפסים להדפסה',
  'printForm.subtitle': 'הטפסים להדפסה שהמרפאה רשמה, והגרסה שכל טופס מודפס ממנה.',

  'printForm.loading': 'טוען את הטפסים להדפסה',
  'printForm.loadFailed': 'לא ניתן לטעון את הטפסים להדפסה',
  'printForm.forbidden': 'אין לך הרשאה לקרוא את הטפסים להדפסה של המרפאה',
  'printForm.bindingsEmpty': 'למרפאה עדיין אין טופס להדפסה רשום',

  'printForm.formsTableLabel': 'טפסים להדפסה רשומים',
  'printForm.form': 'טופס',
  'printForm.bindingCode': 'קוד הטופס',
  'printForm.contextSchema': 'סכימת הנתונים',
  'printForm.anchorTypes': 'מודפס מתוך',
  'printForm.bindingStatus': 'מצב הטופס',
  'printForm.bindingActive': 'בשימוש',
  'printForm.bindingInactive': 'הוצא משימוש',
  'printForm.actions': 'פעולות',
  'printForm.detailsNamed': 'פרטי {name}',
  'printForm.detailsTitle': 'פרטי הטופס',

  'printForm.currentRelease': 'הגרסה הנוכחית',

  'printForm.currentReleaseNamed': 'הגרסה הנוכחית {version}',
  'printForm.noCurrentRelease': 'לא הופעלה גרסה עבור הטופס הזה',
  'printForm.releaseAbsent': 'לא הופעלה',
  'printForm.releaseReadFailed': 'לא ניתן לקרוא את הגרסה הנוכחית של הטופס הזה',
  'printForm.releaseUnread': 'לא נקראה',
  'printForm.releaseVersion': 'גרסה',
  'printForm.pointerRevision': 'גרסת המצביע',
  'printForm.publishedAt': 'פורסם',
  'printForm.activatedAt': 'הופעל',
  'printForm.publishedBy': 'פורסם על ידי',
  'printForm.activatedBy': 'הופעל על ידי',
  'printForm.provider': 'נבנה על ידי',
  'printForm.runtimeApi': 'ממשק זמן ריצה',
  'printForm.bundleSchema': 'סכימת החבילה',
  'printForm.bundleHash': 'טביעת החבילה',

  'printForm.readiness': 'הדפסה',
  'printForm.readinessReady': 'מוכן',
  'printForm.readinessNotReady': 'אינו מוכן',

  'printForm.schemasTitle': 'סכימות נתונים',
  'printForm.schemasEmpty': 'למרפאה עדיין אין סכימת נתונים שפורסמה',
  'printForm.schemaName': 'סכימה',
  'printForm.schemaVersionColumn': 'גרסה',
  'printForm.schemaCode': 'קוד',
  'printForm.schemaDescription': 'תיאור',
  'printForm.schemaDigest': 'טביעת סמנטיקה',
  'printForm.compatibilityTitle': 'נתיבי תאימות',
  'printForm.compatibilityEmpty': 'אין נתיבי מקור תואמים פעילים',
  'printForm.compatibilityLanguage': 'שפה',
  'printForm.compatibilitySource': 'חוזה מקור',
  'printForm.compatibilityBuilder': 'בונה',
  'printForm.compatibilityLegacyIncomplete': 'ל-{count} רשומות ישנות חסר הקשר הדפסה היסטורי מלא',
  'printForm.compatibilitySummary': 'המלאי כולל {sources} גרסאות מקור, {builders} בונים ו-{releases} מהדורות',
  'printForm.compatibilityIssuesTitle': 'עבודת תאימות שנותרה',
  'printForm.compatibilityIssuesEmpty': 'לא נמצאו חוזי הדפסה היסטוריים שאינם נתמכים',
  'printForm.compatibilityIssueBuilder': '{identity}: לא רשום בונה הקשר תואם ({count})',
  'printForm.compatibilityIssueRoute': '{identity}: לטיוטות אין נתיב מקור פעיל ({count})',
  'printForm.compatibilityIssueHistory': '{identity}: הקשר ההדפסה ההיסטורי אינו מלא ({count})',
  'printForm.compatibilityIssueRelease': '{identity}: סמנטיקת ההקשר שפורסמה אינה זמינה ({count})',

  'printForm.editorOpen': 'פתיחת עורך הטפסים להדפסה',
  'printForm.editorOpensInNewTab': 'העורך נפתח בלשונית חדשה, מחובר בחשבון הזה.',
  'printForm.editorChecking': 'בודק את עורך הטפסים להדפסה',
  'printForm.editorUnavailable': 'עורך הטפסים להדפסה אינו מגיב',
  'printForm.editorUnavailableDetail': 'הוא פועל כשירות נפרד. שום דבר שנרשם כאן אינו נפגע; נסה שוב כשהוא יחזור.',

  'printForm.editorPrivileges': 'בעורך אתה: {privileges}',
  'printForm.privilegeEditor': 'עורך',
  'printForm.privilegePublisher': 'מפרסם',
  "quotas.title": "מגבלות ושימוש",
  "quotas.description": "מגבלות הארגון והשימוש הנוכחי.",
  "quotas.employees": "עובדים",
  "quotas.branches": "סניפים",
  "quotas.storage": "אחסון",
  "quotas.storageGB": "אחסון, GB",
  "quotas.gb": "{value} ג״ב",
  "quotas.unlimited": "ללא הגבלה",
  "quotas.used": "בשימוש",
  "quotas.limit": "מגבלה",
  "quotas.reserved": "שמור",
  "quotas.remaining": "זמין",
  "quotas.stateOk": "בתוך המגבלה",
  "quotas.stateTight": "ניצול גבוה",
  "quotas.stateFull": "המגבלה מוצתה",
  "quotas.stateOver": "חריגה מהמגבלה",
  "quotas.usedPercent": "{percent}% בשימוש",
  "quotas.overLimit": "השימוש חורג מהמגבלה. הנתונים נשמרים; הגדלה חסומה.",
  "quotas.estimate": "האחסון כולל קבצים ואומדן יציב של נפח מסד הנתונים. 1 GB = 1,000,000,000 בתים.",
  "quotas.readOnly": "האחסון מלא. קריאה והורדה זמינות. יש לפנות מקום או לבקש הגדלת מכסה כדי להמשיך בשינויים.",
  "quotas.loadFailed": "לא ניתן לטעון את המכסות.",
  "quotas.saveFailed": "המגבלות לא נשמרו. השינויים נשארו בטופס.",
  "quotas.saved": "המגבלות נשמרו.",
  "quotas.save": "שמירת מגבלות",
  "quotas.reload": "טעינה מחדש של המגבלות וביטול השינויים בטופס",
  "quotas.reason": "סיבת השינוי",
  "quotas.conflict": "המגבלות השתנו בשרת. השינויים שלך נשארו בטופס. יש לטעון את המגבלות העדכניות לפני ניסיון נוסף.",
  "quotas.invalid": "יש להזין מגבלות תקינות: סניף אחד לפחות וערכים לא שליליים לעובדים ולאחסון.",
  "quotas.unsaved": "מגבלות מכסה שלא נשמרו",
  "quotas.emptyUnlimited": "השארת מגבלה ריקה מסירה את ההגבלה.",
  "quotas.exceeded": "מכסת הארגון נוצלה. השינויים שלא נשמרו עדיין זמינים.",
  'quotas.dismiss': 'סגירת ההודעה',
}
